import { useSyncExternalStore } from "react";
import { api } from "@/lib/api";

const urlCache = new Map<string, { url: string; exp: number }>();
const inflight = new Map<string, Promise<string>>();

/** Signed B2 URL for a file variant, cached until shortly before expiry. */
export function fileUrl(fileId: string, variant: "thumb" | "large" | "original" = "large"): Promise<string> {
  const key = `${fileId}:${variant}`;
  const hit = urlCache.get(key);
  if (hit && hit.exp > Date.now() + 60_000) return Promise.resolve(hit.url);
  let p = inflight.get(key);
  if (!p) {
    p = api<{ url: string; expiresAt: number }>(`/api/files/${fileId}/url?variant=${variant}`)
      .then((r) => {
        urlCache.set(key, { url: r.url, exp: r.expiresAt });
        return r.url;
      })
      .finally(() => inflight.delete(key));
    inflight.set(key, p);
  }
  return p;
}

/** Local previews for files still uploading, keyed by card id. */
export const localPreviews = new Map<string, string>();

export interface UploadedFile {
  id: string;
  name: string;
  mime: string;
  size: number;
  width: number | null;
  height: number | null;
}

/** Upload straight to B2 with a presigned PUT, then let the server finalize (sniff, thumbnails). */
export async function uploadFile(
  file: File,
  boardId: string,
  onProgress?: (pct: number) => void,
): Promise<UploadedFile> {
  const presign = await api<{ fileId: string; url: string; headers: Record<string, string> }>(
    "/api/files/presign",
    {
      method: "POST",
      json: { boardId, name: file.name, mime: file.type || "application/octet-stream", size: file.size },
    },
  );
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presign.url);
    for (const [k, v] of Object.entries(presign.headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(Math.round((e.loaded / e.total) * 100));
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`upload ${xhr.status}`));
    xhr.onerror = () => reject(new Error("upload failed"));
    xhr.send(file);
  });
  return api<UploadedFile>(`/api/files/${presign.fileId}/complete`, { method: "POST" });
}

export async function imageDimensions(file: File): Promise<{ w: number; h: number } | null> {
  try {
    const bmp = await createImageBitmap(file);
    const d = { w: bmp.width, h: bmp.height };
    bmp.close();
    return d;
  } catch {
    return null;
  }
}

/** Upload progress per card id (0-100), outside React so cards can subscribe individually. */
const progress = new Map<string, number>();
const progressListeners = new Set<() => void>();
export function setUploadProgress(cardId: string, pct: number | null) {
  if (pct === null) progress.delete(cardId);
  else progress.set(cardId, pct);
  for (const l of progressListeners) l();
}
const subscribeProgress = (l: () => void) => {
  progressListeners.add(l);
  return () => progressListeners.delete(l);
};
export const useUploadProgress = (cardId: string): number | null =>
  useSyncExternalStore(subscribeProgress, () => progress.get(cardId) ?? null);

/** Thin bar shown at the bottom of a card while its file uploads. */
export function UploadBar({ id, label }: { id: string; label?: string }) {
  const pct = useUploadProgress(id);
  return (
    <div className="absolute inset-x-0 bottom-0 bg-black/70 px-2 py-1 text-[11px] text-muted">
      <div className="flex items-center justify-between">
        <span className="truncate">{label ?? ""}</span>
        <span>{pct === null ? "…" : `${pct}%`}</span>
      </div>
      <div className="mt-1 h-1 w-full rounded-sm bg-panel-3">
        <div className="h-1 rounded-sm bg-accent transition-[width]" style={{ width: `${pct ?? 2}%` }} />
      </div>
    </div>
  );
}
